import React, { useState, useEffect } from 'react';
import './CheckoutQuestion.css';
import api from '../../services/api';

const CheckoutQuestion = ({ checkoutConfig, couponCode: initialCouponCode, onContinue }) => {
  // Coupon state
  const [coupon, setCoupon] = useState(initialCouponCode || '');
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponValid, setCouponValid] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [discountPreview, setDiscountPreview] = useState(null);

  // Auto-store state
  const [hasStored, setHasStored] = useState(false);
  const [storedProductId, setStoredProductId] = useState(null);

  if (!checkoutConfig) {
    return (
      <div className="checkout-question-error">
        <p>Product configuration is missing. Please contact support.</p>
      </div>
    );
  }

  // Build response object with coupon
  const buildResponse = () => ({
    selected: true,
    product_id: checkoutConfig.product_id,
    product_name: checkoutConfig.product_name,
    beluga_medicine_id: checkoutConfig.beluga_medicine_id,
    pharmacy_id: checkoutConfig.pharmacy_id,
    pharmacy_name: checkoutConfig.pharmacy_name,
    coupon_code: coupon?.trim() || null,
  });

  // Auto-store response when product changes
  useEffect(() => {
    const needsToStore = !hasStored || storedProductId !== checkoutConfig?.product_id;

    if (needsToStore && checkoutConfig) {
      onContinue(buildResponse());
      setHasStored(true);
      setStoredProductId(checkoutConfig.product_id);
    }
  }, [checkoutConfig, hasStored, storedProductId]);

  // Update response when coupon changes (after validation)
  useEffect(() => {
    if (hasStored && couponValid === true) {
      onContinue(buildResponse());
    }
  }, [coupon, couponValid]);

  // Auto-validate coupon from URL on mount
  useEffect(() => {
    if (initialCouponCode && !couponValid && !couponValidating) {
      handleValidateCoupon();
    }
  }, [initialCouponCode]);

  const handleValidateCoupon = async () => {
    const code = coupon?.trim();
    if (!code) {
      setCouponError('Please enter a coupon code');
      setCouponValid(false);
      return;
    }

    setCouponValidating(true);
    setCouponError(null);
    setCouponValid(null);

    try {
      const result = await api.validateCoupon(code, checkoutConfig.product_id);

      if (result.valid) {
        setCouponValid(true);
        const discountType = result.discount_type;
        const discountAmount = result.discount;

        if (discountType === 'fixed') {
          setDiscountPreview(`$${discountAmount} off`);
        } else {
          setDiscountPreview(`${result.value}% off`);
        }

        // Update response with validated coupon
        onContinue(buildResponse());
      } else {
        setCouponValid(false);
        setCouponError(result.message || 'Invalid coupon code');
        setDiscountPreview(null);
      }
    } catch (err) {
      setCouponValid(false);
      setCouponError(err.response?.data?.message || 'Failed to validate coupon');
      setDiscountPreview(null);
    } finally {
      setCouponValidating(false);
    }
  };

  const handleCouponChange = (e) => {
    const value = e.target.value;
    setCoupon(value);
    // Clear validation state when input changes
    if (couponValid !== null) {
      setCouponValid(null);
      setCouponError(null);
      setDiscountPreview(null);
    }
  };

  const handleCouponKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleValidateCoupon();
    }
  };

  const handleContinue = () => {
    onContinue(buildResponse());
  };

  return (
    <div className="checkout-question-container">
      <div className="checkout-product-card">
        <div className="checkout-product-header">
          <h3 className="checkout-product-name">{checkoutConfig.product_name}</h3>
        </div>

        <div className="checkout-product-details">
          {checkoutConfig.pharmacy_name && (
            <div className="checkout-detail-row">
              <span className="checkout-detail-label">Pharmacy:</span>
              <span className="checkout-detail-value">{checkoutConfig.pharmacy_name}</span>
            </div>
          )}

          {checkoutConfig.beluga_medicine_id && (
            <div className="checkout-detail-row">
              <span className="checkout-detail-label">Medicine ID:</span>
              <span className="checkout-detail-value">{checkoutConfig.beluga_medicine_id}</span>
            </div>
          )}
        </div>

        {/* Coupon Input Section */}
        <div className="checkout-coupon-section">
          <label className="checkout-coupon-label">Have a coupon code?</label>
          <div className="checkout-coupon-input-row">
            <input
              type="text"
              className={`checkout-coupon-input ${couponValid === true ? 'valid' : ''} ${couponValid === false ? 'invalid' : ''}`}
              value={coupon}
              onChange={handleCouponChange}
              onKeyPress={handleCouponKeyPress}
              placeholder="Enter code"
              disabled={couponValidating}
            />
            <button
              type="button"
              className="checkout-coupon-apply-btn"
              onClick={handleValidateCoupon}
              disabled={couponValidating || !coupon?.trim()}
            >
              {couponValidating ? '...' : 'Apply'}
            </button>
          </div>

          {couponValid === true && discountPreview && (
            <div className="checkout-coupon-success">
              <span className="success-icon">✓</span> Coupon applied: {discountPreview}
            </div>
          )}

          {couponError && (
            <div className="checkout-coupon-error">
              {couponError}
            </div>
          )}
        </div>

        <div className="checkout-product-footer">
          <p className="checkout-product-note">
            This medication will be prescribed based on your eligibility.
          </p>
        </div>
      </div>

      <button
        className="checkout-continue-button"
        onClick={handleContinue}
        type="button"
      >
        Continue
      </button>
    </div>
  );
};

export default CheckoutQuestion;
